import axios from 'axios';
import Toast from './toast.service';
import { getShareOrigin, goToLogin } from 'src/utils';
import { REACT_APP_API_BASE_URL } from "../env";
import environment from "../environments/environment";
import { DefaultTypeEnum, EvaluationStatusEnum, ScopeEnum, StarStatusEnum } from 'src/pages/interfaces'
import i18n from 'src/i18n'

// 扩展 AxiosRequestConfig 类型
declare module 'axios' {
  interface AxiosRequestConfig {
    skipAuth401?: boolean;
    isShowLoading?: boolean;
  }
}

export const client = axios.create({
  baseURL: REACT_APP_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

let isCurrent401 = false

// 请求拦截器
client.interceptors.request.use(
(config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  
  // 添加额外的请求头
  const uid = localStorage.getItem("uid")
  const referer = document.referrer || getShareOrigin();
  const languageCode = i18n.language
  const currentPage = window.location.pathname;
  const userAgent = navigator.userAgent;

  config.headers["uid"] = uid;
  config.headers["X-Referer"] = referer;
  config.headers["language_code"] = languageCode;
  config.headers["mini_route"] = currentPage;
  config.headers["X-User-Agent"] = userAgent;
  
  // 添加feature-tag header支持（从配置文件读取）
  if (environment?.featureTag) {
    config.headers["feature-tag"] = environment.featureTag;
  }

  // 显示loading
  if (config.isShowLoading) {
    Toast.show({
      icon: 'loading',
      duration: 0,
    });
  }

  return config;
},
(error) => {
  return Promise.reject(error);
}
);

// 响应拦截器
client.interceptors.response.use(
(response) => {
  // 关闭loading
  if (response.config.isShowLoading) {
    Toast.clear();
  }
  return response;
},
async (error) => {
  // 关闭loading
  if (error.config?.isShowLoading) {
    Toast.clear();
  }

  if (error.response?.status === 401) {
    // 检查是否需要跳过401处理
    if (error?.config?.params?.skipAuth401 || error?.config?.skipAuth401) {
      return Promise.reject(error);
    }
    
    if (isCurrent401) { return Promise.reject(error); }
    isCurrent401 = true
    goToLogin();
  }
  return Promise.reject(error);
}
);

// GET 请求
export const get = async (url: string, params?: any, config?: any) => {
  try {
    const response = await client.get(url, { params, ...config });
    return response.data;
  } catch (error) {
    console.error('Error:', error);
    throw error;
  }
};

// POST 请求
export const post = async (url: string, data: any, config?: any) => {
  try {
    const response = await client.post(url, data, config);
    return response.data;
  } catch (error) {
    console.error('Error:', error);
    throw error;
  }
};

// DELETE 请求
export const del = async (url: string, config?: any) => {
  try {
    const response = await client.delete(url, config);
    return response.data;
  } catch (error) {
    console.error('Error:', error);
    throw error;
  }
};

export const api = {
    // ========== 图片上传 ==========

  /**
   * 获取上传文件的全路径
   */
  getFileFullUrl(data: {
    fileName: string,
    scope: ScopeEnum,
    length?: number,
    high?: number,
  }) {
    return post("/sys/upload/get_file_full_url", data);
  },

  /**
   * 获取上传文件的全路径
   */
  getPreOssInfo(data: {
    fileName: string,
    scope?: ScopeEnum,
    length?: number,
    high?: number,
  }) {
    return post("/sys/upload/get_pre_oss_info", data);
  },

  // ========== 翻译 ==========

  /**
   * 批量翻泰语
   */
  translate(data: string[]) {
    return post("/api/translate", data);
  },

  // ========== GBTH 认证管理接口 ==========
  
  /**
   * 用户注册
   */
  register(data: {
    email: string,
    password: string,
    nickname?: string,
  }) {
    return post("/api/auth/register", data);
  },

  /**
   * 用户登录
   */
  login(data: {
    email: string,
    password: string,
    language?: string,
  }) {
    return post("/api/auth/h5_login", data, { skipAuth401: true });
  },

  // 更新用户信息
  updateUserInfo(data:{
    avatar?: string
    nickname?: string
    language?: string,
  }, config?: any){
    return post('/api/mini/user/update_gb_users_info', data, config);
  },

  /**
   * 用户信息
   */
  getUserInfo(config?: any) {
    return post("/api/user/get_user_info", {}, config);
  },

  /**
   * 新增帮卖团长主页
   */
  addSellerGhHome(data: {
    ghId?: number,
    ghName: string,
    ghType: number,
    logo: string
    registrationCode?: string,
  }) {
    return post("/api/mini/gh-home/add_seller_gh_home", data);
  },

  /**
   * 查主页列表
   */
  getGhHomeList() {
    return post("/api/mini/gh-home/get_gh_home_list", {});
  },

   /**
   * 查主页信息
   */
  getGhInfo(data: {
    ghId: number,
  }) {
    return post("/api/mini/gh-home/get_gh_info", data);
  },

  /**
   * 查询用户关注主页的活动Feed流
   */
  getSellerGhHomeActFeed(data: {
    actId?: number,
    pageNum: number,
    pageSize: number,
  }) {
    return post("/api/mini/cus/act/custom_activity_feed", data);
  },

  /**
   * 查询主页当日统计数据
   */
  getGhHomeTodayStat(data: {
    ghId: number,
  }) {
    return post("/api/mini/gh_stat/today_stat", data);
  },

  /**
   * 查询帮卖团长主页的活动Feed流
   */
  getGhHomeActFeed(data: {
    pageNum: number,
    pageSize: number,
    ghId?: number,
  }) {
    return post("/api/mini/act/gh_activity_feed", data);
  },

  /**
   * 活动详情
   */
  getActDetail(data: {
    actId: number,
  }) {
    return post("/api/mini/cus/act/custom_activity_detail", data);
  },

  /**
   * 查询活动用户订单列表
   */
  getActUserOrderList(data: {
    pageNum: number,
    pageSize: number,
    actId: number,
  }) {
    return post("/api/mini/cus/act/act_user_order", data);
  },

  /**
   * 新增用户地址
   */
  addUserAddress(data: {
    ///是否默认地址：0-否，1-是
    defaultType?: DefaultTypeEnum,
    ///详细地址
    detailAddress?: string;
    ///区/县
    district?: string;
    districtId?: number;
    ///手机号码
    mobile?: string;
    ///邮政编码（5位）
    postalCode?: string;
    ///省份
    province?: string;
    provinceId?: number;
    ///收件人姓名
    recipientName?: string;
    ///区/分区
    subDistrict?: string;
    subDistrictId?: number;
  }) {
    return post("/api/mini/user-address/add_user_address", data);
  },

  /**
   * 查询用户地址列表
   */
  getUserAddressPage(data: {
    page: number,
    pageSize: number,
  }) {
    return post("/api/mini/user-address/get_user_address_page", data);
  },

  /**
   * 查询用户地址详情
   */
  getUserAddressDetail(data: {
    addressId: number,
  }) {
    return post("/api/mini/user-address/get_user_address_detail", data);
  },

  /**
   * 更新用户地址
   */
  updateUserAddress(data: {
    addressId: number,
    ///是否默认地址：0-否，1-是
    defaultType?: DefaultTypeEnum,
    ///详细地址
    detailAddress?: string;
    ///区/县
    district?: string;
    districtId?: number;
    ///手机号码
    mobile?: string;
    ///邮政编码（5位）
    postalCode?: string;
    ///省份
    province?: string;
    provinceId?: number;
    ///收件人姓名
    recipientName?: string;
    ///区/分区
    subDistrict?: string;
    subDistrictId?: number;
  }) {
    return post("/api/mini/user-address/update_user_address", data);
  },

  /**
   * 删除用户地址
   */
  deleteUserAddress(data: {
    addressId: number,
  }) {
    return post("/api/mini/user-address/delete_user_address", data);
  },

  /**
   * 查询用户默认地址详情
   */
  getUserDefaultAddressDetail() {
    return get("/api/mini/user-address/get_user_default_address_detail", {});
  },

  /**
   * 创建订单
   */
  createOrder(data: {
    actId: number,
    addressId: number,
    remark?: string,
    items: Array<{
      actGoodsId: number,
      buyNum: number,
    }>
  }) {
    return post("/api/mini/order/create_order", data);
  },

  /**
   * 查询可被帮卖的供货母团活动
   */
  queryGhHelpParentAct(data: {
    pageNum: number,
    pageSize: number,
    ghId?: number,
  }) {
    return post("/api/mini/act/query_gh_help_parent_act", data);
  },

  /**
   * 帮卖团长开启帮卖活动
   */
  openHelpAct(data: {
    actId: number,
    ghId: number,
  }) {
    return post("/api/mini/act/open_help_act", data);
  },

  /**
   * 查询用户订单列表
   */
  getCustomerOrderList(data: {
    ghId?: number,
    page: number,
    pageSize: number,
    orderStatus?: number,
    shipStatus?: number,
    orderNo?: string,
    startTime?: string,
    endTime?: string,
  }) {
    return post("/api/mini/order/get_customer_order_list", data);
  },

  /**
   * 查询帮卖团长订单列表
   */
  getBusinessOrderList(data: {
    actId?: number,
    ghId?: number,
    page: number,
    pageSize: number,
    orderStatus?: number,
    shipStatus?: number,
    orderNo?: string,
    startTime?: string,
    endTime?: string,
  }) {
    return post("/api/mini/order/get_business_order_list", data);
  },

  /**
   * 查询帮卖团长关注的供货母团活动数量
   */
  getRelationSuppliesNum(data: {
    ghId?: number,
  }) {
    return post("/api/mini/gh-home/get_relation_supplies_num", data);
  },

  // 获取OAuth2授权链接
  getOauthUrl(data: {
    redirectUrl: string; // 重定向地址
    loginType: number; // 登录类型
    appId?: string; // 应用ID
    state?: string; // 状态值
    authScopeType?: number;
  }) {
    return post('/api/oauth2/getOauthUrl', data);
  },
  // OAuth2登录回调，通过授权码获取token
  codeToToken(data: {
    code: string; // 第三方授权code
    redirectUrl: string; // 重定向地址（谷歌和facebook需要）
    loginType: number; // 登录类型 10 20 30 40 50 60
    appId: string; // 应用ID
    state: string; // 状态值
    language?: string; // 语言
  }) {
    return post('/api/oauth2/codeToToken', data, { skipAuth401: true });
  },

  /// 查询主页余额
  getGhBalance(data: {
    ghId: number,
  }) {
    return post("/api/mini/account/get_gh_balance", data);
  },

  /**
   * 查询主页流水
   * /api/mini/account/query_gh_account_fund_flow
   */
  queryGhAccountFundFlow(data: {
    ghId: number,
    pageNum: number,
    pageSize: number,
  }) {
    return post("/api/mini/account/query_gh_account_fund_flow", data);
  },

  /// 支付订单
  payOrder(data: {
    orderNo: string,
  }) {
    return post("/api/mini/pay/pay_order", data);
  },

  ///C支付状态
  getUserOrderStatus(data: {
    orderNo: string,
  }) {
    return get("/api/mini/order/get_user_order_status", data);
  },

  ///主页粉丝数
  getFansNum(data: {
    ghId: number,
  }) {
    return post("/api/mini/gh-home/get_fans_num", data);
  },

  ///主页粉丝列表
  getFansList(data: {
    ghId: number,
    pageNum: number,
    pageSize: number,
  }) {
    return post("/api/mini/gh-home/get_fans_list", data);
  },

  // ========== 银行卡管理 ==========

  /**
   * 发送短信验证码
   */
  sendSmsCode(data: {
    phone: string,
    smsType: number,
  }) {
    return post("/api/pay/card/send_verify_code", data);
  },

  /**
   * 查询银行卡列表
   */
  getBankCardList(data: {
    ghId: number,
    pageNum: number,
    pageSize: number,
  }) {
    return post("/api/pay/card/list", data);
  },

  /**
   * 绑定银行卡
   */
  bindBankCard(data: {
    ghId?: number,
    bankName: string,
    bankCode: string,
    accountName: string,
    accountNo: string,
    currency?: string,
    phone: string,
    verifyCode: string,
    defaultCard?: number, // 是否默认银行卡 0 否 1 是
  }) {
    return post("/api/pay/card/bind", data);
  },

  /**
   * 开户银行
   */
  queryBankList() {
    return get("/api/pay/card/query_bank_list", {});
  },

  /**
   * 设置默认提现银行卡
   * /api/pay/card/set_default
   */
  setDefaultBankCard(data: {
    cardId: number,
  }) {
    return post("/api/pay/card/set_default", data);
  },

  /**
   * 删除银行卡
   * /api/pay/card/delete
   */
  deleteBankCard(data: {
    cardId: number,
  }) {
    return post("/api/pay/card/delete", data);
  },

  // ========== 提现相关接口 ==========

  /**
   * 分页查询提现记录
   */
  queryWithdrawRecordPage(data: {
    ghId: number,
    pageNum: number,
    pageSize: number,
  }) {
    return post("/api/mini/act/query_withdraw_record_page", data);
  },

  /**
   * 查询提现信息
   */
  queryWithdrawInfo(data: {
    ghId: number,
  }) {
    return post("/api/mini/act/query_withdraw_info", data);
  },

  /**
   * 提现
   * /api/mini/act/post_bank_withdraw
   */
  postBankWithdraw(data: {
    ghId: number,
    applyWithdrawMoney: number,
    cardId: number,
  }) {
    return post("/api/mini/act/post_bank_withdraw", data);
  },

  /**
   * 查询提现详情
   */
  queryWithdrawDetail(data: {
    withdrawId: number,
    fundId?: number,
  }) {
    return post("/api/mini/act/query_withdraw_detail", data);
  },

  /**
   * 查询可被帮卖的供货商品列表
   */
  queryHelpParentGoods(data: {
    ghId: number,
    pageNum: number,
    pageSize: number,
  }) {
    return post("/api/mini/act/query_gh_help_parent_goods", data);
  },

  /**
   * 查询商品信息
   */
  queryGoodsInfo(data: {
    ghId: number,
    goodsId: number,
  }) {
    return post("/api/mini/act/query_goods_info", data);
  },

  /**
   * 团长开团供货商品
   */
  openHelpGoodsAct(data: {
    goodsId?: number,
    ghId: number,
    actTitle?: string,
    actDetail?: string,
    startTime?: number,
    endTime?: number,
  }) {
    return post("/api/mini/act/open_help_goods_act", data);
  },

  /**
   * 更新活动信息
   */
  updateAct(data: {
    actId: number,
    actTitle: string,
    actDetail: string,
    startTime: number,
    endTime: number,
  }) {
    return post("/api/mini/act/update_act", data);
  },

  /**
   * 我关注的帮卖
   */
  getFollowingActNum(data: {
    pageNum: number,
    pageSize: number,
  }) {
    return post("/api/mini/gh-fans/my-following", data);
  },

  /**
   * /api/mini/gh-fans/unbind
   * 取消关注帮卖
   */
  unbindFollowingAct(data: {
    fansId: number,
  }) {
    return post("/api/mini/gh-fans/unbind", data);
  },

  /**
   * 提交订单评价
   * /api/mini/order-comment/submit_order_comment
   */
  submitOrderComment(data: {
    orderNo: string,
    commentText: string,
    // 评分星级（1-5星）
    star: number,
    ghId: number,
    commentMediaList?: string[], // 评价图片列表
    isAnonymous?: number, // 是否匿名评价 0 否 1 是
  }) {
    return post("/api/mini/order-comment/submit_order_comment", data);
  },

  /**
   * 小程序C端查询订单详情
   * /api/mini/order/get_customer_order_detail
   */
  getCustomerOrderDetail(data: {
    orderNo: string,
  }) {
    return post("/api/mini/order/get_customer_order_detail", data);
  },

  /**
   * 小程序B端口查询订单详情
   * /api/mini/order/get_business_order_detail
   */
  getBusinessOrderDetail(data: {
    orderNo: string,
    ghId?: number,
  }) {
    return post("/api/mini/order/get_business_order_detail", data);
  },

  /**
   * 小程序C端确认收货
   * /api/mini/order/confirm_receive
   */
  confirmReceived(data: {
    orderNo: string,
  }) {
    return post("/api/mini/order/confirm_receive", data);
  },

  /**
   * 查询待结算佣金的订单
   * /api/mini/gh_stat/query_pending_order_page
   */
  queryPendingOrderPage(data: {
    ghId?: number,
    actId?: number,
    page: number,
    pageSize: number,
  }) {
    return post("/api/mini/gh_stat/query_pending_order_page", data);
  },

  /**
   * 待结算佣金订单详情
   * /api/mini/gh_stat/query_pending_commission_detail
   */
  queryPendingCommissionDetail(data: {
    orderNo: string,
    ghId?: number,
  }) {
    return post("/api/mini/gh_stat/query_pending_commission_detail", data);
  },

  /**
   * 查询团长最近浏览过的供货商品
   * /api/mini/act/query_gh_latest_view_goods
   */
  queryGhLatestViewGoods(data: {
    ghId: number,
    pageNum: number,
    pageSize: number,
  }) {
    return post("/api/mini/act/query_gh_latest_view_goods", data);
  },

  /**
   * 查询团长评价列表
   * /api/mini/order-comment/get_order_comment_list_by_gh_id
   */
  queryOrderCommentListByGhId(data: {
    actId?: number,
    ghId?: number,
    pageNum: number,
    pageSize: number,
  }) {
    return post("/api/mini/order-comment/get_order_comment_list_by_gh_id", data);
  },
  
  /**
   * 评价管理列表查询
   * /api/pc/order-comment-business/get_order_comment_manage_list
   */
  queryOrderCommentManageList(data: {
    actId?: number,
    ghId?: number,
    pageNum: number,
    pageSize: number,
    commentStatus?: number, // 状态：10-待审核，20-已通过，30-已拒绝
  }) {
    return post("/api/pc/order-comment-business/get_order_comment_manage_list", data);
  },

  /**
   * 获取评价统计数据get
   * /api/pc/order-comment-business/get_order_comment_stat
   */
  queryOrderCommentStat(data: {
    actId?: number,
  }) {
    return post("/api/pc/order-comment-business/get_order_comment_stat", data);
  },

  /**
   * 评价点赞/取消点赞
   * /api/mini/order-comment/toggle_order_comment_like
   */
  toggleOrderCommentLike(data: {
    commentId: number,
    starStatus?: StarStatusEnum,
  }) {
    return post("/api/mini/order-comment/toggle_order_comment_like", data);
  },

  /**
   * 审核评价
   * /api/pc/order-comment-business/audit_order_comment
   */
  auditOrderComment(data: {
    commentId: number,
    commentStatus: EvaluationStatusEnum,
  }) {
    return post("/api/pc/order-comment-business/audit_order_comment", data);
  },

  /**
   * 查询用户最近浏览的活动的Feed流列表，只返回进行中的活动
   * /api/mini/cus/act/query_latest_view_activity_feed
   */
  queryLatestViewActivityFeed(data: {
    pageNum: number,
    pageSize: number,
  }) {
    return post("/api/mini/cus/act/query_latest_view_activity_feed", data);
  },

  /** ============省市区联动相关============== */
  /**
   * 查询省份列表
   * /api/th-address/provinces
   */
  queryProvinces() {
    return get("/api/th-address/provinces");
  },

  /**
   * 根据省份ID查询区列表
   * /api/th-address/districts
   */
  queryDistricts(data: {
    provinceId: number,
  }) {
    return get("/api/th-address/districts", data);
  },
  
  /**
   * 根据区ID查询分区列表
   * /api/th-address/sub-districts
   */
  querySubDistricts(data: {
    districtId: number,
  }) {
    return get("/api/th-address/sub-districts", data);
  },

  /**
   * 根据ID查询地址
   * /api/th-address/get_th_address_by_id
   */
  queryAddressById(data: {
    addressId: number,
  }) {
    return get("/api/th-address/get_th_address_by_id", data);
  },

  /**
   * 申请退款
   * /api/mini/order-refund/create_refund
   */
  createRefund(data: {
    orderNo: string,
    refundReason: string,
    // remark?: string,
    refundPics?: string[],
    // refundTotalGoodsPrice?: number,
    // refundTotalShipFee?: number,
    // refundTotalCustomsFee?: number,
    // items: Array<{
    //   orderItemId: number,
    //   itemRefundCount: number,
    //   itemRefundGoodsPrice: number,
    //   itemRefundShipFee?: number,
    //   itemRefundCustomsFee?: number,
    // }>,
  }) {
    return post("/api/mini/order-return/create_return_order", data);
  },

};